import { Settings2 } from "lucide-react";
import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { StandaloneSpeech } from "../hooks/use-standalone-speech";
import { dictationBusy } from "../speech/controller";

export function OptionalSpeechPreferences({ speech }: { speech: StandaloneSpeech }) {
  const { preferences, capabilities, state } = speech;
  const busy = dictationBusy(state);
  const error = state.dictationError;
  const [popover, setPopover] = useState({ busy, error, open: Boolean(busy || error) });
  if (popover.busy !== busy || popover.error !== error) {
    setPopover({ busy, error, open: busy || error ? true : popover.open });
  }
  const titleId = useId();

  return (
    <Popover
      onOpenChange={(open) => setPopover((current) => ({ ...current, open }))}
      open={popover.open}
    >
      <PopoverTrigger asChild>
        <Button
          aria-label="Open speech settings"
          size="mobileTouchIcon"
          type="button"
          variant="ghost"
        >
          <Settings2 aria-hidden="true" className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" aria-labelledby={titleId} className="w-80 space-y-4">
        <div>
          <h2 className="font-medium text-sm" id={titleId}>
            Optional speech
          </h2>
          <p className="mt-1 text-muted-foreground text-xs">Dictation and read aloud are opt-in.</p>
        </div>
        <div className="space-y-3 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={preferences.dictation}
              onChange={(event) => speech.setPreference("dictation", event.target.checked)}
            />
            Enable dictation
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={preferences.readAloud}
              onChange={(event) => speech.setPreference("readAloud", event.target.checked)}
            />
            Enable Read aloud
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={preferences.readRepliesAutomatically}
              disabled={!preferences.readAloud}
              onChange={(event) =>
                speech.setPreference("readRepliesAutomatically", event.target.checked)
              }
            />
            Read replies automatically
          </label>
        </div>
        {preferences.dictation && (
          <div className="space-y-2">
            {!capabilities.dictation.available && <p>{capabilities.dictation.reason}</p>}
            <p aria-live="polite">
              {state.dictation === "starting"
                ? "Preparing microphone…"
                : state.dictation === "recording"
                  ? `Recording · ${state.seconds} / 60 seconds`
                  : state.dictation === "transcribing"
                    ? "Transcribing…"
                    : state.dictation === "confirm"
                      ? "Confirm transcription or discard recording."
                      : ""}
            </p>
            {["recording", "confirm"].includes(state.dictation) && (
              <Button type="button" onClick={speech.stopDictation}>
                {state.dictation === "confirm" ? "Transcribe recording" : "Stop and transcribe"}
              </Button>
            )}
            {busy && (
              <Button type="button" variant="ghost" onClick={speech.cancelDictation}>
                Cancel dictation
              </Button>
            )}
            {state.dictationError && <p role="status">{state.dictationError}</p>}
          </div>
        )}
        {preferences.readAloud && !capabilities.readAloud.available && (
          <p>{capabilities.readAloud.reason}</p>
        )}
        <p className="text-muted-foreground text-xs">
          Standalone speech uses metered APIs. Dictation uploads only after Stop or confirmation.
          Cancellation cannot delete audio already sent or refund generated speech.
        </p>
      </PopoverContent>
    </Popover>
  );
}
